import { Instagram, ArrowUpRight, Sparkles, Droplets, SprayCan, Gem, CarFront, HandMetal, CalendarCheck, Crown } from 'lucide-react';
import { Reveal, SectionHeading } from './Reveal';

const GH_INSTAGRAM = 'https://www.instagram.com/detailedbyghshowroom/';
const GH_HANDLE = '@detailedbyghshowroom';
const GH_BLUE = '#2E9BF0';

const PARTNERS = [
  {
    id: 'glass-house',
    name: 'Glass House Showroom Detailing',
    logo: '/images/glasshouse-logo.png',
    motto: 'Cleaner than clean. Shinier than new.',
    tagline: 'Premium showroom detailing focused on keeping vehicles cleaner, shinier, and looking their best.',
    description:
      'Glass House Showroom Detailing specializes in cosmetic vehicle care, including premium detailing, paint correction, ceramic coatings, and interior care.',
    instagram: GH_INSTAGRAM,
    handle: GH_HANDLE,
    services: [
      { icon: Sparkles, title: 'Premium Detailing', text: "Interior & exterior detailing designed to restore and maintain your vehicle's appearance." },
      { icon: Gem, title: 'Paint Correction', text: 'Professional paint correction to improve the appearance of imperfections and restore gloss.' },
      { icon: SprayCan, title: 'Ceramic Coatings', text: 'Long-lasting paint protection and enhanced shine.' },
      { icon: CarFront, title: 'Interior Care', text: 'Deep interior cleaning and protection.' },
      { icon: HandMetal, title: 'Hand Wash', text: 'Careful hand washing for a cleaner, safer finish.' },
      { icon: Droplets, title: 'Maintenance Washes', text: 'Regular maintenance washes to keep your vehicle looking fresh between full details.' },
      { icon: Crown, title: 'Showroom Detailing', text: 'Premium comprehensive detailing for customers who want their vehicle looking its absolute best.' },
    ],
    disclaimer:
      'Glass House Showroom Detailing is an independent automotive service provider and is not owned or operated by ALAS Velocity. Services are provided directly by Glass House Showroom Detailing.',
  },
];

const PartnerCard = ({ p }) => (
  <Reveal>
    <article data-testid={`partner-card-${p.id}`} className="border border-white/10 bg-surface">
      <div className="h-[3px] w-full bg-gradient-to-r from-gold via-[#2E9BF0] to-gold" />
      <div className="p-8 md:p-12">
        <div className="flex flex-wrap items-start justify-between gap-8">
          <div className="flex flex-col items-start gap-6 sm:flex-row">
            <div
              className="shrink-0 overflow-hidden border"
              style={{ borderColor: `${GH_BLUE}55`, background: '#050505' }}
              data-testid={`partner-logo-${p.id}`}
            >
              {p.logo ? (
                <img src={p.logo} alt={`${p.name} logo`} className="h-16 w-auto max-w-full object-cover sm:h-20" loading="lazy" />
              ) : (
                <span className="flex h-20 w-20 items-center justify-center font-display text-2xl tracking-widest" style={{ color: GH_BLUE, background: `${GH_BLUE}0d` }}>
                  {p.name.split(' ').map((w) => w[0]).slice(0, 2).join('')}
                </span>
              )}
            </div>
            <div>
              <span className="text-[10px] font-medium uppercase tracking-[0.3em] text-gold">Featured Partner</span>
              <h3 className="font-display mt-2 text-3xl tracking-tight text-white sm:text-4xl">{p.name}</h3>
              {p.motto && <p className="mt-1 text-xs font-medium uppercase tracking-[0.3em]" style={{ color: GH_BLUE }}>{p.motto}</p>}
              <p className="mt-3 max-w-xl text-base font-light leading-relaxed text-white/65">{p.tagline}</p>
              <p className="mt-2 max-w-xl text-sm font-light leading-relaxed text-white/45">{p.description}</p>
            </div>
          </div>
          <div className="flex flex-col items-start gap-3">
            <a
              data-testid={`partner-visit-${p.id}`}
              href={p.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-3 bg-gold px-8 py-4 text-xs font-semibold uppercase tracking-[0.22em] text-ink transition-all duration-300 hover:bg-gold-bright"
            >
              Visit Glass House
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <a
              data-testid={`partner-instagram-${p.id}`}
              href={p.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 border border-white/15 px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-white/80 transition-colors duration-300 hover:border-[#2E9BF0] hover:text-[#2E9BF0]"
            >
              <Instagram className="h-4 w-4" /> Follow on Instagram
            </a>
            <span data-testid={`partner-handle-${p.id}`} className="text-xs tracking-[0.15em] text-white/40">{p.handle}</span>
          </div>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-px border border-white/5 bg-white/5 sm:grid-cols-2 lg:grid-cols-4">
          {p.services.map((s) => {
            const Icon = s.icon;
            return (
              <div
                key={s.title}
                data-testid={`gh-service-${s.title.toLowerCase().replace(/\s+/g, '-')}`}
                className="group flex h-full flex-col bg-surface p-7 transition-colors duration-500 hover:bg-surface-light"
              >
                <Icon className="h-6 w-6 text-gold transition-colors duration-300 group-hover:text-[#2E9BF0]" strokeWidth={1.25} />
                <h4 className="mt-4 text-xs font-semibold uppercase tracking-[0.2em] text-white">{s.title}</h4>
                <p className="mt-2 text-sm font-light leading-relaxed text-white/50">{s.text}</p>
              </div>
            );
          })}
          <a
            href={p.instagram}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`partner-book-${p.id}`}
            className="group flex flex-col items-start justify-center p-7 transition-colors duration-500"
            style={{ background: `${GH_BLUE}0d` }}
          >
            <span className="text-xs font-semibold uppercase tracking-[0.2em]" style={{ color: GH_BLUE }}>Book with Glass House</span>
            <span className="mt-2 flex items-center gap-2 text-sm font-light text-white/60 transition-colors group-hover:text-white">
              Message them on Instagram <ArrowUpRight className="h-4 w-4" />
            </span>
          </a>
        </div>

        <p data-testid={`partner-disclaimer-${p.id}`} className="mt-8 border-t border-white/10 pt-6 text-xs font-light leading-relaxed text-white/35">
          {p.disclaimer}
        </p>
      </div>
    </article>
  </Reveal>
);

const VehicleServices = () => (
  <section id="vehicle-services" data-testid="vehicle-services-section" className="scroll-mt-24 border-y border-white/5 bg-[#0a0a0a] py-28 lg:py-36">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <SectionHeading eyebrow="Vehicle Services" title={<>Trusted automotive<br /><span className="italic text-gold">partners.</span></>} />
      <Reveal>
        <p className="-mt-8 mb-14 max-w-2xl text-base font-light leading-relaxed text-white/55 md:-mt-12 md:mb-20">
          Keep your vehicle looking its best with premium cosmetic automotive services from our trusted partners. Luxury vehicles. Trusted automotive partners. One premium ecosystem.
        </p>
      </Reveal>
      <div className="space-y-10">
        {PARTNERS.map((p) => (
          <PartnerCard key={p.id} p={p} />
        ))}
      </div>
    </div>
  </section>
);

export default VehicleServices;
